import { MessageSquare, Pencil, Code, Rocket, CheckCircle, Sparkles } from 'lucide-react';
import {
    motion,
    useInView,
    useScroll,
    useTransform,
    useSpring,
    useReducedMotion,
} from 'motion/react';
import { useMemo, useRef } from 'react';

export function Process() {
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const isInView = useInView(ref, { once: true, margin: '-120px' });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });

    const orb1YRaw = useTransform(scrollYProgress, [0, 1], ['0%', '45%']);
    const orb2YRaw = useTransform(scrollYProgress, [0, 1], ['0%', '-45%']);
    const orb1Y = useSpring(orb1YRaw, { stiffness: 90, damping: 22 });
    const orb2Y = useSpring(orb2YRaw, { stiffness: 90, damping: 22 });

    const steps = useMemo(
        () => [
            {
                icon: MessageSquare,
                title: 'Beratung & Konzept',
                description:
                    'Wir klären Ziele, Zielgruppe und Anforderungen. Danach haben Sie einen klaren Plan für Timing, Umfang und Prioritäten.',
                micro: 'Kickoff & Scope',
            },
            {
                icon: Pencil,
                title: 'Design & Prototyp',
                description:
                    'Modernes Design + klickbarer Prototyp. Sie sehen früh, wie sich alles anfühlt – Feedback fließt direkt ein.',
                micro: 'Look & Feel',
            },
            {
                icon: Code,
                title: 'Entwicklung',
                description:
                    'Saubere Umsetzung mit Fokus auf Performance, Struktur und Wartbarkeit. Alles modular und zukunftssicher.',
                micro: 'Build',
            },
            {
                icon: Rocket,
                title: 'Testing & Launch',
                description:
                    'Responsiveness, Performance und letzte Details. Danach geht die Seite stressfrei live – inklusive Checkliste.',
                micro: 'Go Live',
            },
            {
                icon: CheckCircle,
                title: 'Support & Wartung',
                description:
                    'Updates, Optimierungen und Weiterentwicklung. Damit Ihre Website langfristig schnell, stabil und aktuell bleibt.',
                micro: 'Care',
            },
        ],
        []
    );

    return (
        <section id="ablauf" className="relative py-24 px-4 overflow-hidden" ref={ref}>
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50" />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.18) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                    maskImage: 'radial-gradient(circle at 50% 35%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            {/* Ambient blobs with parallax */}
            <motion.div
                className="absolute -top-24 left-[-120px] w-[520px] h-[520px] rounded-full blur-3xl opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(59,130,246,0.35), transparent 60%)',
                    y: orb1Y,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.08, 1], opacity: [0.16, 0.28, 0.16] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute -bottom-28 right-[-140px] w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(168,85,247,0.35), transparent 60%)',
                    y: orb2Y,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.1, 1], opacity: [0.15, 0.27, 0.15] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 26 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/70 backdrop-blur px-4 py-2 shadow-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
                        <span className="text-sm text-gray-700">Mein Prozess</span>
                        <span className="ml-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              Klar. Transparent. Effizient.
            </span>
                    </div>

                    <h2 className="mt-6 text-5xl md:text-6xl tracking-tight">
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Von der Idee
            </span>{' '}
                        <span className="text-gray-900">zum Launch</span>
                    </h2>

                    <p className="mt-5 text-xl text-gray-600 max-w-2xl mx-auto">
                        Ein strukturierter Ablauf, damit Ihr Projekt planbar bleibt – mit schnellen Feedback-Schleifen und klaren
                        Ergebnissen nach jedem Schritt.
                    </p>
                </motion.div>

                {/* Timeline / Steps */}
                <div className="relative">
                    {/* center line */}
                    <div className="absolute left-1/2 top-0 hidden lg:block h-full w-px -translate-x-1/2 bg-gradient-to-b from-indigo-200 via-purple-200 to-pink-200" />

                    <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            const isLeft = index % 2 === 0;

                            return (
                                <motion.div
                                    key={step.title}
                                    className={`relative ${isLeft ? 'lg:pr-10' : 'lg:pl-10'} ${
                                        isLeft ? 'lg:text-right' : 'lg:text-left'
                                    }`}
                                    initial={{ opacity: 0, y: 28 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.55, delay: 0.08 * index }}
                                >
                                    {/* Node */}
                                    <div
                                        className={`hidden lg:block absolute top-8 left-1/2 -translate-x-1/2 ${
                                            isLeft ? 'lg:translate-x-[-50%]' : 'lg:translate-x-[-50%]'
                                        }`}
                                        aria-hidden="true"
                                    >
                                        <div className="relative">
                                            <div className="h-4 w-4 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 shadow-md" />
                                            <div className="absolute -inset-4 rounded-full bg-gradient-to-br from-indigo-600/20 to-purple-600/20 blur-xl opacity-70" />
                                        </div>
                                    </div>

                                    <motion.div
                                        className="group relative overflow-hidden rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg hover:shadow-2xl transition-shadow"
                                        whileHover={{ y: -6 }}
                                        transition={{ type: 'spring', stiffness: 240, damping: 20 }}
                                    >
                                        {/* gradient glow */}
                                        <div className="pointer-events-none absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20" />

                                        <div className="relative p-6">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center shadow-md">
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                    <div className="text-left">
                                                        <div className="text-xs text-gray-600">Schritt {index + 1}</div>
                                                        <div className="text-sm font-medium text-gray-900">{step.micro}</div>
                                                    </div>
                                                </div>

                                                <span className="inline-flex items-center rounded-full bg-black/5 px-3 py-1 text-xs text-gray-700 border border-gray-200/70">
                          {step.title}
                        </span>
                                            </div>

                                            <h3 className="mt-4 text-xl text-gray-900">
                                                {step.title}
                                            </h3>

                                            <p className="mt-2 text-gray-600 leading-relaxed">
                                                {step.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Bottom CTA strip */}
                    <motion.div
                        className="mt-10"
                        initial={{ opacity: 0, y: 18 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.65, duration: 0.6 }}
                    >
                        <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg overflow-hidden">
                            <div className="p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                <div>
                                    <div className="text-sm text-gray-600">Bereit für den Start?</div>
                                    <div className="text-lg font-semibold text-gray-900">
                                        Kostenloses Erstgespräch – wir klären in 20 Minuten den nächsten Schritt.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-white font-medium shadow-md hover:from-indigo-700 hover:to-purple-700 transition-colors"
                                >
                                    Termin anfragen
                                    <Rocket className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
